import { memo } from "react";
import { Badge, Button } from "@core/components";
import { icons } from "@core/assets";
import { useViewerTranslate } from "@modules/viewers/hooks/useTranslate";
import { renderDocumentStatus, renderDocumentStatusText } from "@/core";
import type { TDocDownload } from "@/modules/docs/documents";

// Minimal doc info needed by the header — structural typing keeps viewers/ clean.

type ViewerHeaderProps = {
  doc: TDocDownload;
  tools?: React.ReactNode;
  onClose: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
};

export const ViewerHeader = memo(function ViewerHeader({
  doc,
  tools,
  onClose,
  onDownload,
  isDownloading,
}: ViewerHeaderProps) {
  const { t } = useViewerTranslate();

  return (
    <header className="w-full h-[3.6875rem] bg-white border-b-[1px] border-neutral-200 py-5 px-4 flex items-center justify-between">
      {/* left: back button + doc name + approval status */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          aria-label={t("back")}
          onClick={onClose}
          leftIcon={icons["arrow-left"]}
          iconStrokeWidth={1.5}
          size={"sm"}
          variant={"secondary"}
        />
        <h5 className="text-neutral-900 font-medium capitalize">{doc.name}</h5>
        {doc.approval_status != null && (
          <Badge
            title={renderDocumentStatusText(doc.approval_status)}
            className={`${renderDocumentStatus(
              doc.approval_status,
            )} w-[5.3rem] h-[2rem] flex items-center justify-center font-medium`}
          />
        )}
      </div>

      {/* spacer */}
      <div className="flex-1" />

      {/* right: tools slot + download + close */}
      <div className="flex items-center gap-3">
        {tools != null && (
          <div className="flex items-center gap-1">{tools}</div>
        )}
        <div className="flex items-center gap-3">
          {onDownload && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("download")}
              onClick={onDownload}
              isLoading={isDownloading}
              iconOnly
              leftIcon={icons["download"]}
              iconStrokeWidth={1.6}
            />
          )}

          <Button
            variant="ghost"
            size="sm"
            aria-label={t("close")}
            onClick={onClose}
            iconOnly
            leftIcon={icons["x-close"]}
            iconStrokeWidth={1.6}
          />
        </div>
      </div>
    </header>
  );
});
